import { useState, useEffect, useMemo } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Container, Breadcrumbs, Link, Button, Paper, useMediaQuery, useTheme } from '@mui/material';
import { Favorite as FavoriteIcon, FavoriteBorder as FavoriteBorderIcon, PlaylistAdd as PlaylistAddIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import AddToListButton from '../components/lists/AddToListButton';
import { ImageSlider } from '../components/ImageSlider';
import AuthPromptDialog from '../components/AuthPromptDialog';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useProducts } from '../contexts/ProductsContext';
import { ProductCard } from '../components/ProductCard';
import type { Product } from '../types/product';
import '../styles/product.css';
import { formatGBP } from '../utils/currency';

const ProductPage = () => {
  const { id } = useParams<{ id: string }>();
  const { getProductById, loading, error, products } = useProducts();
  const { isAuthenticated, isFavorite, toggleFavorite } = useAuth();
  const [listAnchorEl, setListAnchorEl] = useState<null | HTMLElement>(null);
  const [authPromptOpen, setAuthPromptOpen] = useState(false);
  const [afterAuth, setAfterAuth] = useState<(() => void) | null>(null);
  const [product, setProduct] = useState<Product | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchProduct = () => {
      if (!id) return;
      const productData = getProductById(parseInt(id));
      if (!productData) {
        setProduct(null);
        return;
      }
      
      setProduct(productData);
    };

    fetchProduct();
  }, [id, getProductById]);

  // Set body ID for page-specific styling
  useEffect(() => {
    const previousId = document.body.id;
    document.body.id = 'product-page';
    return () => {
      if (previousId) {
        document.body.id = previousId;
      } else {
        document.body.removeAttribute('id');
      }
    };
  }, []);

  // Related products (prefer same category, then fill), up to 4
  const relatedProducts = useMemo(() => {
    if (!product) return [] as Product[];
    const all = Array.isArray(products) ? products : [];
    const currentId = product.id;
    const cat = (product.category ?? '').toLowerCase();
    // Sort newest first
    const sortNewest = (list: Product[]) =>
      [...list].sort((a, b) => {
        const aTime = (a.updatedAt ?? a.createdAt ?? new Date(0)).valueOf();
        const bTime = (b.updatedAt ?? b.createdAt ?? new Date(0)).valueOf();
        return bTime - aTime;
      });

    const sameCategory = sortNewest(
      all.filter(p => p.id !== currentId && (p.category ?? '').toLowerCase() === cat)
    );

    if (sameCategory.length >= 4) return sameCategory.slice(0, 4);

    // Fill remaining slots with other products (excluding current and already included)
    const pickedIds = new Set(sameCategory.map(p => p.id));
    const others = sortNewest(
      all.filter(p => p.id !== currentId && !pickedIds.has(p.id))
    );

    return [...sameCategory, ...others].slice(0, 4);
  }, [product, products]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography>Loading product...</Typography>
      </Container>
    );
  }

  if (error || !product) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="error" gutterBottom>{error || 'Product not found'}</Typography>
        <Button 
          component={RouterLink} 
          to="/" 
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Back to Products
        </Button>
      </Container>
    );
  }

  // Prepare product images array (fallback to single image or placeholder)
  const productImages: string[] = (product.images && product.images.length > 0)
    ? product.images
    : [product.image || 'https://placehold.co/600x400/efefef/999999?text=No+Image'];

  

  return (
    <Container maxWidth="lg" sx={{ py: 4 }} className="product-page">
      {/* Breadcrumbs */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 4, display: { xs: 'none', md: 'flex' } }}>
        <Link
          component={RouterLink}
          to="/"
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          {/* <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" /> */}
          Home
        </Link>
        <Link
          component={RouterLink}
          to="/"
          color="inherit"
        >
          Products
        </Link>
        <Typography color="text.primary">{product.name}</Typography>
      </Breadcrumbs>

      <Button
        className="back-button"
        component={RouterLink}
        to="/"
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 3, display: { xs: 'none', md: 'inline-flex' } }}
      >
        Back to Products
      </Button>

      <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, mb: 4 }} className="product-hero">
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }} className="product-content">
          {/* Product Image */}
          <Box sx={{ flex: 1, order: { xs: 1, md: 2 } }} className="product-gallery">
            {productImages.length > 1 ? (
              <ImageSlider images={productImages} height="auto" />
            ) : (
              <Box
              
                component="img"
                src={productImages[0]}
                alt={product.name}
                sx={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: 1,
                }}
              />
            )}
          </Box>
          
          {/* Product Details */}
          <Box sx={{ flex: 1, order: { xs: 2, md: 1 } }}>
            <Typography variant="h4" component="h1" gutterBottom>
              {product.name}
            </Typography>

            
            
            
            <Box className="productprice" sx={{ my: 3 }}>
              <Typography variant="h5" color="primary" fontWeight="bold" gutterBottom>
                {formatGBP(Number(product.price ?? 0))}
              </Typography>
              {/* {product.originalPrice !== undefined && product.originalPrice !== null && (
                <Typography variant="body2" color="text.disabled" sx={{ textDecoration: 'line-through' }}>
                  {formatGBP(Number(product.originalPrice ?? 0))}
                </Typography>
              )} */}
            </Box>

            <Box className="productdescription" sx={{ mb: 4 }}>
              <Typography variant="body1" paragraph>
                {product.description || 'No description available.'}
              </Typography>
            </Box>
            

            {/* {product.category && (
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                Category: {product.category}
              </Typography>
            )} */}
                        
            
            {/* Action buttons: Save (favorite), Add to list, View Product */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'row', sm: 'row' }, gap: 1.5, mt: 2, width: '100%' }}>
              <Button
                className="savebutton"
                variant={isAuthenticated && isFavorite(product.id) ? 'contained' : 'outlined'}
                color={isAuthenticated && isFavorite(product.id) ? 'error' : 'inherit'}
                startIcon={isAuthenticated && isFavorite(product.id) ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                aria-label={isAuthenticated && isFavorite(product.id) ? 'Remove from saved' : 'Save product'}
                sx={{ width: { xs: '15%', md: 'auto' }, flex: { xs: '0 0 15%', md: '0 0 auto' } }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (isAuthenticated) {
                    toggleFavorite(product.id);
                  } else {
                    setAfterAuth(() => () => toggleFavorite(product.id));
                    setAuthPromptOpen(true);
                  }
                }}
              />

              <Button
                className="add-to-list-button"
                variant="outlined"
                color="inherit"
                startIcon={<PlaylistAddIcon />}
                aria-label="Add to list"
                sx={{ width: { xs: '15%', md: 'auto' }, flex: { xs: '0 0 15%', md: '0 0 auto' } }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (isAuthenticated) {
                    setListAnchorEl(e.currentTarget);
                  } else {
                    const target = e.currentTarget as HTMLElement;
                    setAfterAuth(() => () => setListAnchorEl(target));
                    setAuthPromptOpen(true);
                  }
                }}
              />
              {listAnchorEl && (
                <AddToListButton
                  productId={product.id}
                  anchorEl={listAnchorEl}
                  onClose={() => setListAnchorEl(null)}
                />
              )}

              {product.url ? (
                <Button
                  className="viewproduct"
                  variant="contained"
                  color="primary"
                  size="large"
                  endIcon={isMobile ? undefined : <OpenInNewIcon />}
                  sx={{ width: { xs: '60%', md: 'auto' }, flex: { xs: '0 0 60%', md: '0 0 auto' } }}
                  href={product.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Product
                </Button>
              ) : (
                <Button variant="contained" color="primary" size="large" fullWidth disabled>
                  No product link
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <Box sx={{ mb: 6 }} className="related-products">
          <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 2 }}>
            Related products
          </Typography>
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)'
            },
            gap: 3
          }}>
            {relatedProducts.map((rp) => (
              <ProductCard key={rp.id} product={rp} className="product-card" />
            ))}
          </Box>
        </Box>
      )}

      <AuthPromptDialog 
        open={authPromptOpen} 
        onClose={() => setAuthPromptOpen(false)} 
        onSuccess={() => {
          setAuthPromptOpen(false);
          if (afterAuth) {
            afterAuth();
            setAfterAuth(null);
          }
        }}
      />
    </Container>
  );
};

export default ProductPage;
